import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { MerchantStatusCell } from "Components/Hunter/Merchants/MerchantsList/MerchantStatusCell";
import {
  MerchantStatus,
  type UnassignedMerchantListItemDto,
} from "Infrastructure/Api/Api";
import { Link } from "Navigation/Link";
import { formatDate } from "Utils/DateUtils";
import { useTableLocalization } from "Utils/TableLocalizationUtils";
import { appUrl } from "Utils/UrlUtils";
import {
  type MRT_ColumnDef,
  type MRT_TableOptions,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { useMemo } from "react";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Hunter.UnassignedMerchants;

type Props = {
  data: Array<UnassignedMerchantListItemDto>;
  isRefetching?: boolean;
  isLoading?: boolean;
  onRowClick: (merchant: UnassignedMerchantListItemDto) => void;
  rowCount: number;
  tableProps: Partial<MRT_TableOptions<UnassignedMerchantListItemDto>>;
};

export const UnassignedMerchantsTable = ({
  data,
  isRefetching,
  isLoading,
  onRowClick,
  rowCount,
  tableProps,
}: Props) => {
  const { t } = useResource();
  const localization = useTableLocalization();

  const columns = useMemo<Array<MRT_ColumnDef<UnassignedMerchantListItemDto>>>(
    () => [
      {
        accessorKey: "merchantStatus",
        header: t(RESOURCES.TableHeaders.Status),
        Cell: ({ row }) => (
          <MerchantStatusCell status={row.original.merchantStatus} />
        ),
        filterVariant: "multi-select",
        filterSelectOptions: [
          {
            label: t(Resources.Merchant.MerchantStatus.InDraft),
            value: MerchantStatus.InDraft,
          },
          {
            label: t(
              Resources.Merchant.MerchantStatus.InvitedWaitingForConfirmation,
            ),
            value: MerchantStatus.InvitedWaitingForConfirmation,
          },
          {
            label: t(Resources.Merchant.MerchantStatus.Onboarding),
            value: MerchantStatus.Onboarding,
          },
          {
            label: t(
              Resources.Merchant.MerchantStatus.WaitingForPaymentTerminal,
            ),
            value: MerchantStatus.WaitingForPaymentTerminal,
          },
          {
            label: t(Resources.Merchant.MerchantStatus.Active),
            value: MerchantStatus.Active,
          },
          {
            label: t(
              Resources.Merchant.MerchantStatus.BlockedFinancialServices,
            ),
            value: MerchantStatus.BlockedFinancialServices,
          },
        ],
        size: 0,
      },
      {
        accessorKey: "party.companyNumber",
        accessorFn: (row) => row.party?.companyNumber ?? "",
        header: t(RESOURCES.TableHeaders.CompanyNumber),
        size: 70,
      },
      {
        accessorKey: "party.companyName",
        accessorFn: (row) => row.party?.companyName ?? "",
        header: t(RESOURCES.TableHeaders.CompanyName),
        size: 290,
      },
      {
        accessorKey: "dateCreated",
        header: t(RESOURCES.TableHeaders.DateCreated),
        size: 200,
        filterVariant: "date",
        Cell: ({ cell }) => formatDate(cell.getValue<Date>()),
      },
      {
        id: "detail",
        header: t(RESOURCES.TableHeaders.Detail),
        enableColumnFilter: false,
        enableSorting: false,
        size: 80,
        muiTableBodyCellProps: { sx: { p: 0 } },
        Cell: ({ row }) => (
          <Link
            to={`${appUrl("merchant-detail")}/${row.original.publicID}`}
            onClick={(event) => event.stopPropagation()}
            aria-label={t(RESOURCES.OpenDetailLabel)}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              width: "100%",
              height: "100%",
              paddingLeft: 8,
              color: "inherit",
              textDecoration: "none",
            }}
          >
            <OpenInNewIcon fontSize="small" />
          </Link>
        ),
      },
    ],
    [t],
  );

  const table = useMaterialReactTable({
    ...tableProps,
    data,
    columns,
    rowCount,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    localization,
    state: {
      ...tableProps.state,
      isLoading,
      showProgressBars: isRefetching,
    },
    initialState: {
      ...tableProps.initialState,
      density: "compact",
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => onRowClick(row.original),
      sx: { cursor: "pointer" },
    }),
    muiTablePaperProps: { sx: { borderRadius: "10px" } },
  });

  return <MaterialReactTable table={table} />;
};
